// Pagination types and utilities

export interface PaginationParams {
  page: number;
  pageSize: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface PaginationMetadata {
  currentPage: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startIndex: number;
  endIndex: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: PaginationMetadata;
}

export const DEFAULT_PAGE_SIZE = 25;
export const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;

/**
 * Calculate pagination metadata
 */
export function calculatePagination(
  totalItems: number,
  currentPage: number,
  pageSize: number
): PaginationMetadata {
  const totalPages = Math.ceil(totalItems / pageSize);
  const safePage = Math.max(1, Math.min(currentPage, totalPages || 1));
  const startIndex = (safePage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalItems);

  return {
    currentPage: safePage,
    pageSize,
    totalItems,
    totalPages,
    hasNextPage: safePage < totalPages,
    hasPreviousPage: safePage > 1,
    startIndex,
    endIndex,
  };
}

/**
 * Paginate an array of items
 */
export function paginateArray<T>(
  items: T[],
  page: number,
  pageSize: number
): PaginatedResponse<T> {
  const pagination = calculatePagination(items.length, page, pageSize);
  const data = items.slice(pagination.startIndex, pagination.endIndex);

  return { data, pagination };
}

/**
 * Generate page numbers for pagination UI
 * Shows: 1 ... 4 5 [6] 7 8 ... 20
 */
export function generatePageNumbers(
  currentPage: number,
  totalPages: number,
  maxVisible: number = 7
): (number | 'ellipsis')[] {
  if (totalPages <= maxVisible) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: (number | 'ellipsis')[] = [];
  const sidePages = Math.floor((maxVisible - 3) / 2); // Reserve space for first, last, and ellipsis

  // Always show first page
  pages.push(1);

  if (currentPage <= sidePages + 2) {
    // Near the start
    for (let i = 2; i <= Math.min(maxVisible - 2, totalPages - 1); i++) {
      pages.push(i);
    }
    if (totalPages > maxVisible - 1) {
      pages.push('ellipsis');
    }
  } else if (currentPage >= totalPages - sidePages - 1) {
    // Near the end
    pages.push('ellipsis');
    for (let i = Math.max(2, totalPages - maxVisible + 3); i < totalPages; i++) {
      pages.push(i);
    }
  } else {
    // In the middle
    pages.push('ellipsis');
    for (let i = currentPage - sidePages; i <= currentPage + sidePages; i++) {
      pages.push(i);
    }
    pages.push('ellipsis');
  }

  // Always show last page
  if (totalPages > 1) {
    pages.push(totalPages);
  }

  return pages;
}
